import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './index.css';
import logo from './logo.svg';

import Home from './pages/Home';
import ChargerDetail from './pages/ChargerDetail';
import AddCharger from './pages/AddCharger';
import SessionsLog from './pages/SessionsLog';
import AddRFIDCard from './pages/AddRFIDCard';
import ViewRFIDCards from './pages/ViewRFIDCards';
import Reports from './pages/Reports';
import OrganisationDetail from './pages/OrganisationDetail';
import AddTariff from './pages/AddTariff';
import ViewTariffs from './pages/ViewTariffs';
import EditTariff from './pages/EditTariff';
import ChargerPay from './pages/ChargerPay';
import ViewEVSE from './pages/ViewEVSE';
import DashboardOverview from './pages/DashboardOverview';
import EnergyInsights from './pages/EnergyInsights';
import axios from 'axios';

import Login from './pages/Login';
import Register from './pages/Register';

import LoadManagement from './pages/LoadManagement';
import LoadManagementList from './pages/LoadManagementList';


export const AppContext = createContext(null);
export function useAppContext() { return useContext(AppContext); }

function isLoggedIn() {
  return !!localStorage.getItem('srlk_token');
}

function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('srlk_user') || 'null');
  } catch {
    return null;
  }
}

const IconMenu = () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
const IconHome = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const IconZap  = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;

const IconCard = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="2" y="5" width="20" height="14" rx="2.5" />
    <line x1="2" y1="10" x2="22" y2="10" />
    <line x1="6" y1="15" x2="10" y2="15" />
  </svg>
);

const IconGauge = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 22a10 10 0 100-20 10 10 0 000 20z" />
    <path d="M12 12l4-4" /><path d="M12 6v.01" />
  </svg>
);

const IconClockHistory = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M3 3v5h5" />
    <path d="M3.05 13A9 9 0 106 5.3L3 8" />
    <path d="M12 7v5l4 2" />
  </svg>
);

const IconChargingPump = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="4" y="3" width="10" height="18" rx="1.5" />
    <line x1="4" y1="9" x2="14" y2="9" />
    <circle cx="9" cy="6" r="1" fill="currentColor" stroke="none" />
    <path d="M14 8h2a2 2 0 012 2v7a1.5 1.5 0 003 0v-8l-2-2" />
  </svg>
);

const IconPricetagCoin = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12.586 2.586A2 2 0 0011.172 2H4a2 2 0 00-2 2v7.172a2 2 0 00.586 1.414l8.704 8.704a2.426 2.426 0 003.42 0l6.58-6.58a2.426 2.426 0 000-3.42l-8.704-8.704z" />
    <circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const IconChevron = ({ className }) => <svg className={className} width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>;

function NavItem({ icon, label, active, collapsed, onClick }) {
  return (
    <div className="nav-tooltip-wrap">
      <button className={`nav-item ${active ? 'active' : ''}`} onClick={onClick}>
        {icon}
        <span className="nav-label">{label}</span>
      </button>
      {collapsed && <span className="nav-tooltip">{label}</span>}
    </div>
  );
}

function NavGroup({ icon, label, subLinks, groupPathPrefix, collapsed, location, navigate, isOpen, onToggle }) {
  const groupActive = location.pathname.startsWith(groupPathPrefix);

  return (
    <div className="nav-tooltip-wrap" style={{ position: 'relative' }}>
      <div className={`nav-group-header ${groupActive ? 'has-active' : ''}`} onClick={() => onToggle()}>
        <span className="nav-group-header-left">
          {icon}
          <span className="nav-group-label">{label}</span>
        </span>
        <IconChevron className={`nav-chevron ${isOpen ? 'open' : ''}`} />
      </div>

      {collapsed && <span className="nav-tooltip">{label}</span>}

      {!collapsed && isOpen && (
        <div className="nav-group-items">
          {subLinks.map(sub => (
            <button
              key={sub.path}
              className={`nav-subitem ${location.pathname === sub.path ? 'active' : ''}`}
              onClick={() => navigate(sub.path)}
            >
              <span className="nav-subitem-dot" />
              {sub.label}
            </button>
          ))}
        </div>
      )}

      {collapsed && isOpen && (
        <div className="nav-flyout">
          {subLinks.map(sub => (
            <button
              key={sub.path}
              className={`nav-flyout-item ${location.pathname === sub.path ? 'active' : ''}`}
              onClick={() => { navigate(sub.path); onToggle(true); }}
            >
              {sub.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Sidebar({ liveConnected, collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [tariffMenuOpen, setTariffMenuOpen] = useState(location.pathname.startsWith('/tariffs'));
  const [cardsMenuOpen, setCardsMenuOpen] = useState(location.pathname.startsWith('/rfid-cards'));
  const [evseMenuOpen, setEvseMenuOpen] = useState(location.pathname.startsWith('/add-charger') || location.pathname.startsWith('/evse'));
  const [dashboardMenuOpen, setDashboardMenuOpen] = useState(location.pathname.startsWith('/dashboard'));

  // Close any open flyout whenever the sidebar collapses, so nothing
  // sticks out over the page content while collapsed.
  useEffect(() => {
    if (collapsed) {
      setTariffMenuOpen(false);
      setCardsMenuOpen(false);
      setEvseMenuOpen(false);
      setDashboardMenuOpen(false);
    }
  }, [collapsed]);

  const overviewLink = { path: '/', label: 'Overview', icon: <IconHome /> };
  const sessionLogLink = { path: '/sessions', label: 'Session Log', icon: <IconClockHistory /> };

  const evseSubLinks = [
    { path: '/add-charger', label: 'Add EVSE' },
    { path: '/evse',        label: 'View EVSE' },
  ];

  const cardsSubLinks = [
    { path: '/rfid-cards/add',  label: 'Add RFID Card' },
    { path: '/rfid-cards',      label: 'View RFID Cards' },
  ];

  const tariffSubLinks = [
    { path: '/tariffs/create', label: 'Create Tariff' },
    { path: '/tariffs',        label: 'View Tariffs' },
  ];

  const dashboardSubLinks = [
    { path: '/dashboard',         label: 'Overview' },
    { path: '/dashboard/energy',  label: 'Energy Insights' },
  ];

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <div className="sidebar-logo-row">
          <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title={collapsed ? 'Expand menu' : 'Collapse menu'}>
            <IconMenu />
          </button>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src={logo} alt="SRLK EV Charge" style={{ height: 44 }} />
              <span className="brand-text" style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Management Dashboard</span>
            </div>
          )}
        </div>
      </div>

      <nav className="sidebar-nav">
        <NavItem
          icon={overviewLink.icon}
          label={overviewLink.label}
          active={location.pathname === overviewLink.path}
          collapsed={collapsed}
          onClick={() => navigate(overviewLink.path)}
        />

        <NavGroup
          icon={<IconPricetagCoin />}
          label="Insights"
          subLinks={dashboardSubLinks}
          groupPathPrefix="/dashboard"
          collapsed={collapsed}
          location={location}
          navigate={navigate}
          isOpen={dashboardMenuOpen}
          onToggle={(forceClose) => setDashboardMenuOpen(prev => forceClose ? false : !prev)}
        />

        <NavGroup
          icon={<IconChargingPump />}
          label="EVSE Management"
          subLinks={evseSubLinks}
          groupPathPrefix="/evse"
          collapsed={collapsed}
          location={{ pathname: location.pathname === '/add-charger' ? '/add-charger' : location.pathname }}
          navigate={navigate}
          isOpen={collapsed ? evseMenuOpen : (evseMenuOpen || location.pathname === '/add-charger')}
          onToggle={(forceClose) => setEvseMenuOpen(prev => forceClose ? false : !prev)}
        />

        <NavGroup
          icon={<IconPricetagCoin />}
          label="Tariff Management"
          subLinks={tariffSubLinks}
          groupPathPrefix="/tariffs"
          collapsed={collapsed}
          location={location}
          navigate={navigate}
          isOpen={tariffMenuOpen}
          onToggle={(forceClose) => setTariffMenuOpen(prev => forceClose ? false : !prev)}
        />

        <NavGroup
          icon={<IconCard />}
          label="RFID Cards"
          subLinks={cardsSubLinks}
          groupPathPrefix="/rfid-cards"
          collapsed={collapsed}
          location={location}
          navigate={navigate}
          isOpen={cardsMenuOpen}
          onToggle={(forceClose) => setCardsMenuOpen(prev => forceClose ? false : !prev)}
        />

        <NavItem
          icon={sessionLogLink.icon}
          label={sessionLogLink.label}
          active={location.pathname === sessionLogLink.path}
          collapsed={collapsed}
          onClick={() => navigate(sessionLogLink.path)}
        />

        <NavItem
          icon={<IconGauge />}
          label="Load Management"
          active={location.pathname.startsWith('/load-management')}
          collapsed={collapsed}
          onClick={() => navigate('/load-management')}
        />
      </nav>

      <div className="sidebar-footer">
        <div className="connection-dot">
          <div className={`dot ${liveConnected ? 'live' : ''}`} />
          <span className="dot-label">{liveConnected ? 'Live updates active' : 'Connecting...'}</span>
        </div>
      </div>
    </aside>
  );
}

const IconBell = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);
const IconAlertCircle = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);


function notifTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr + 'Z')) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr + 'Z').toLocaleDateString('en-GB');
}

function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_BASE}/notifications/`);
      setNotifications(res.data.notifications);
    } catch (e) {
      console.error('Failed to fetch notifications', e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (notif) => {
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const handleBellClick = async () => {
    const wasOpen = open;
    setOpen(prev => !prev);
    if (!wasOpen) {
      // Opening the dropdown — mark all activity entries as read so the
      // badge clears once the person has actually seen them.
      try {
        await axios.post(`${API_BASE}/notifications/mark-all-read`);
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      } catch (e) {
        console.error('Failed to mark notifications as read', e);
      }
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="notif-bell-wrap" ref={wrapRef}>
      <button className="notif-bell-btn" onClick={handleBellClick}>
        <IconBell />
        {unreadCount > 0 && <span className="notif-bell-badge">{unreadCount}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">Notifications</div>
          {notifications.length === 0 ? (
            <div className="notif-empty">You're all caught up.</div>
          ) : (
            notifications.map(notif => (
              <div className="notif-item" key={notif.id} onClick={() => handleItemClick(notif)}>
                <div className={`notif-item-icon ${notif.type}`}><IconAlertCircle /></div>
                <div style={{ flex: 1 }}>
                  <div className="notif-item-title">{notif.title}</div>
                  {notif.description && <div className="notif-item-desc">{notif.description}</div>}
                  {notif.created_at && <div className="notif-item-time">{notifTimeAgo(notif.created_at)}</div>}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const API_BASE = 'https://api.cpms-srlkevcharge.com';

const IconLogOut = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

function TopbarProfile() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);
  const user = getCurrentUser();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const displayName = user.full_name || user.email;
  const initial = displayName.charAt(0).toUpperCase();

  const handleLogout = () => {
    localStorage.removeItem('srlk_token');
    localStorage.removeItem('srlk_user');
    navigate('/login');
  };

  return (
    <div className="topbar-profile-wrap" ref={wrapRef}>
      <button className="topbar-profile-btn" onClick={() => setOpen(prev => !prev)}>
        <div className="topbar-profile-avatar">{initial}</div>
        <span className="topbar-profile-greeting">Hello, {user.full_name ? user.full_name.split(' ')[0] : user.email}</span>
      </button>

      {open && (
        <div className="topbar-profile-dropdown">
          <div className="topbar-profile-dropdown-header">
            <div className="topbar-profile-dropdown-name">{displayName}</div>
            <div className="topbar-profile-dropdown-email">{user.email}</div>
          </div>
          <button className="topbar-profile-dropdown-item danger" onClick={handleLogout}>
            <IconLogOut /> &nbsp;Log Out
          </button>
        </div>
      )}
    </div>
  );
}

function Topbar() {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <header className="topbar">
      <img src={logo} alt="SRLK EV Charge" style={{ height: 44 }} />
      <NotificationBell />
      <span className="topbar-time">{time.toLocaleTimeString('en-GB')}</span>
      <TopbarProfile />
    </header>
  );
}

function ToastContainer({ toasts }) {
  return <div className="toast-container">{toasts.map(t => <div key={t.id} className={`toast ${t.type}`}>{t.message}</div>)}</div>;
}

function AppShell() {
  const [liveConnected, setLiveConnected] = useState(false);
  const [liveEvents, setLiveEvents] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login');
    }
  }, [navigate]);

  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  useEffect(() => {
    let ws, retryTimer;
    function connect() {
      ws = new WebSocket('wss://api.cpms-srlkevcharge.com/ws/dashboard');
      ws.onopen = () => setLiveConnected(true);
      ws.onmessage = (e) => { try { const data = JSON.parse(e.data); setLiveEvents(prev => [data, ...prev.slice(0, 49)]); } catch {} };
      ws.onclose = () => { setLiveConnected(false); retryTimer = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    }
    connect();
    return () => { clearTimeout(retryTimer); ws && ws.close(); };
  }, []);

  return (
    <AppContext.Provider value={{ liveEvents, showToast }}>
      <div className="layout">
        <Sidebar
          liveConnected={liveConnected}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(prev => !prev)}
        />
        <main className={`main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Topbar />
          <div className="page">
            <Routes>
              <Route path="/"                 element={<Home />} />
              <Route path="/dashboard"        element={<DashboardOverview />} />
              <Route path="/dashboard/energy" element={<EnergyInsights />} />
              <Route path="/load-management/:locationId" element={<LoadManagement />} />
              <Route path="/load-management" element={<LoadManagementList />} />
              <Route path="/charger/:id"      element={<ChargerDetail />} />
              <Route path="/add-charger"      element={<AddCharger />} />
              <Route path="/evse"             element={<ViewEVSE />} />
              <Route path="/sessions"         element={<SessionsLog />} />
              <Route path="/rfid-cards"       element={<ViewRFIDCards />} />
              <Route path="/rfid-cards/add"   element={<AddRFIDCard />} />
              <Route path="/reports"          element={<Reports />} />
              <Route path="/organisation/:id" element={<OrganisationDetail />} />
              <Route path="/tariffs"          element={<ViewTariffs />} />
              <Route path="/tariffs/create"   element={<AddTariff />} />
              <Route path="/tariffs/:id/edit" element={<EditTariff />} />
            </Routes>
          </div>
        </main>
        <ToastContainer toasts={toasts} />
      </div>
    </AppContext.Provider>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/pay/:qrCodeId" element={<ChargerPay />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/*" element={<AppShell />} />
      </Routes>
    </Router>
  );
}